import { supabase } from "../supabase/SupabaseClient";
import { enElRango } from "../shared/periodoVales";
import {
  BUE,
  RECIENTES,
  RGD,
  SERIE,
  VALES_DESDE,
  corteDe,
  cuentaDeVales,
} from "../shared/talonario";
import { traerTodas } from "./paginar";

/* Las cargas de urea, tal como las registró el bot (foto del vale mediante).
 *
 * ESTA PANTALLA NO CARGA NADA: el alta es el flujo del bot —foto obligatoria,
 * lectura del vale, cotejo con el padrón— y duplicarlo acá sería tener dos
 * puertas con reglas distintas. El hub MIRA el histórico y, con
 * `admin.borrar`, saca la carga que entró mal (el vale doble, la foto
 * repetida). No hay editar, a propósito: una carga mal leída se borra y se
 * vuelve a mandar el vale — corregir a mano acá dejaría la foto diciendo una
 * cosa y la fila otra.
 *
 * La pared es la RLS: `ver.combustible` para leer, `admin.borrar` (hoy sólo
 * Igna) para borrar. La pantalla no decide nada de eso, sólo lo cuenta.
 */

const CAMPOS =
  "id,fecha,interno,litros,vale,km,serie,producto,quien,quien_nombre," +
  "fuente,foto,media_id,nota,creado_en,vale_manual,vale_editado_por,vale_editado_en";

/* El rango [desde, hasta], ambos inclusive, en días de Argentina.
 *
 * El tope de 999 es el techo real de una página de PostgREST (ver
 * `sinLimitesFalsos.test`); un mes de cargas anda por las decenas, así que
 * alcanzará por años — y si un día no alcanza, se achica el rango. */
export async function traerCargasUrea(desde, hasta) {
  /* El corte es en días ARGENTINOS (`shared/periodoVales`): `fecha` es
   * timestamptz y filtrar por el texto `YYYY-MM-DD` correría el corte tres
   * horas — la carga de las 22:00 aparecería en el día siguiente. */
  const q = enElRango(
    supabase
      .from("urea_cargas")
      .select(CAMPOS)
      .order("fecha", { ascending: false })
      .limit(999),
    desde, hasta);
  const { data, error } = await q;
  if (error) throw error;
  return data || [];
}

/* Una carga sola, por id. La pide el buscador (`?ver=`): de ella sale el DÍA
 * al que hay que llevar el calendario para que la fila esté en pantalla. */
export async function traerCargaUrea(id) {
  const { data, error } = await supabase
    .from("urea_cargas")
    .select(CAMPOS)
    .eq("id", id)
    .maybeSingle();
  if (error) throw error;
  return data || null;
}

/* Borrar de verdad (sin papelera): la carga duplicada no es un hecho a
 * conservar, es un error de registro — el hecho real sigue en la fila que
 * queda y en la foto del vale.
 *
 * El `.select()` es la confirmación: la RLS de DELETE filtra en silencio, así
 * que sin permiso la consulta «anda» y borra cero filas. Cero filas acá ES el
 * error, y se dice. */
export async function borrarCargaUrea(id) {
  const { data, error } = await supabase
    .from("urea_cargas")
    .delete()
    .eq("id", id)
    .select("id");
  if (error) throw error;
  if (!data || data.length === 0)
    throw new Error("No se borró nada: no tenés permiso.");
  return data[0];
}

/* Editar el NÚMERO de vale que el bot leyó mal (Nico/Igna, 24/09). La foto no se
 * toca — es y sigue siendo la verdad; esto sólo corrige el número tipeado cuando
 * la lectura falló. Va por un RPC gateado (`editar.vale_urea`, sólo 3 personas)
 * que además deja registro de quién y cuándo. El servidor normaliza el número
 * como el bot (saca ceros a la izquierda). Devuelve la fila actualizada. */
export async function editarValeUrea(id, vale) {
  const { data, error } = await supabase.rpc("editar_vale_urea", {
    p_id: id,
    p_vale: vale,
  });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

/* ══ LA CUENTA DE VALES FALTANTES, COMO EL PARTE ═════════════════════════════
 *
 * Nico, 11/10: el parte decía «Faltan 86089 vales» por un vale mal leído y se
 * agregó el reinicio («/valesdeurea-reset» en el bot). La cuenta en sí está en
 * `shared/talonario` (`contarTalonario`, apareada con `urea.huecos`); acá se
 * traen los tres datos que necesita: desde dónde se cuenta, las cargas de cada
 * talonario desde ahí, y los números anulados. */

/* Desde dónde se cuenta cada talonario (`urea_corte_vales()`), o `null` si no
 * se pudo leer. `null` NO es «sin corte»: quien llama cuenta desde el piso del
 * 1/9, que muestra más faltantes y nunca menos — la misma dirección que toma el
 * bot cuando no puede leer el suyo. */
export async function traerCorteVales() {
  const { data, error } = await supabase.rpc("urea_corte_vales");
  if (error || !Array.isArray(data) || data.length === 0) return null;
  return data;
}

/* Los números anulados o sin usar que siguen vigentes, de los dos talonarios.
 * Sin poder leerlos devuelve `null`: NO es «no hay anulados», y la cuenta lo
 * dice en vez de sacar nada. */
export async function traerAnuladosUrea() {
  const { data, error } = await supabase
    .from("urea_vales_anulados")
    .select("numero,serie")
    .is("revertido_en", null)
    .order("numero", { ascending: true })
    .limit(999);
  if (error) return null;
  return data || [];
}

/* Las cargas de UN talonario desde su corte: TODAS, de a páginas.
 *
 * Con ancla, el talonario entero y sin filtro de fecha —una fecha mal leída
 * puede ser cualquiera: el 9560 está cargado «del 2024»—, y Buenos Aires pasa
 * las mil filas en unos meses. PostgREST corta en mil sin avisar, y una cuenta
 * sobre un pedazo diría «faltan 0» mirando la mitad: por eso `traerTodas`.
 *
 * Por fecha, `fecha >= 'YYYY-MM-DD'` con el texto tal cual, que es el mismo
 * corte que hace el bot (ver `contarTalonario`). */
export async function traerTalonarioUrea(id, corte) {
  return traerTodas(() => {
    let q = supabase
      .from("urea_cargas")
      .select("id,vale,serie,fecha,creado_en", { count: "exact" });
    q = SERIE[id] ? q.eq("serie", SERIE[id]) : q.is("serie", null);
    if (!corte?.desde_vale) q = q.gte("fecha", corte?.desde_fecha || VALES_DESDE);
    return q.order("id", { ascending: true });
  });
}

/* Las últimas `RECIENTES` cargas de UN talonario por `id`, sin mirar la fecha:
 * desempatan cuál es la serie cuando por fecha hay más de un tramo (es lo que
 * pide `urea.tope_reciente`). Sin poder leerlas devuelve `null`: la cuenta
 * sigue sin desempate, como el bot. */
export async function traerRecientesUrea(id) {
  try {
    let q = supabase.from("urea_cargas").select("id,vale");
    q = SERIE[id] ? q.eq("serie", SERIE[id]) : q.is("serie", null);
    const { data, error } = await q.order("id", { ascending: false }).limit(RECIENTES);
    if (error) return null;
    return data || [];
  } catch {
    return null;
  }
}

/* La cuenta de los dos talonarios, lista para mostrar (ver `cuentaDeVales`).
 * Si las cargas no se pueden traer, LANZA: una cuenta hecha sobre nada diría
 * «no falta ninguno», y eso no se puede mostrar. */
export async function traerCuentaDeVales() {
  const [cortes, anulados] = await Promise.all([
    traerCorteVales(),
    traerAnuladosUrea(),
  ]);
  const [bue, rgd, recBue, recRgd] = await Promise.all([
    traerTalonarioUrea(BUE, corteDe(cortes, BUE)),
    traerTalonarioUrea(RGD, corteDe(cortes, RGD)),
    traerRecientesUrea(BUE),
    traerRecientesUrea(RGD),
  ]);
  return cuentaDeVales({
    filas: { [BUE]: bue, [RGD]: rgd },
    cortes,
    anulados,
    recientes: { [BUE]: recBue, [RGD]: recRgd },
  });
}
